import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { RateCard } from '../components/RateCard'
import { SponsorSlot } from '../components/SponsorSlot'
import { TrendPill } from '../components/TrendPill'
import {
  ASIA_EUROPE_ANSWER,
  ASIA_EUROPE_DESCRIPTION,
  ASIA_EUROPE_FAQ,
  ASIA_EUROPE_HEADING,
  ASIA_EUROPE_TITLE,
} from '../data/asia-europe-seo.mjs'
import { tradeBlurb, tradeBucket, tradeBySlug, tradeName } from '../data/trades'
import { T, useLocale, useT } from '../i18n'
import { usePageMeta } from '../lib/meta'
import {
  type IndexRow,
  type IndexTrend,
  type LaneRow,
  fmtPrice,
  getIndexTrend,
  getIndexWeekly,
  getLanes,
} from '../lib/rates'

export function TradePage() {
  const t = useT()
  const locale = useLocale()
  const { slug = '' } = useParams()
  const trade = tradeBySlug(slug)
  const tradeId = trade?.id
  const tName = trade ? tradeName(trade, locale) : null
  const tBlurb = trade ? tradeBlurb(trade, locale) : ''
  const [lanes, setLanes] = useState<LaneRow[]>([])
  const [series, setSeries] = useState<IndexRow[]>([])
  const [trend, setTrend] = useState<IndexTrend | null>(null)

  useEffect(() => {
    if (tradeId == null) return
    // Classify by destination (frm_trade_bucket), not the unreliable line_category_id column.
    getLanes()
      .then((all) => setLanes(all.filter((l) => tradeBucket(l.pod_code) === tradeId)))
      .catch(() => {})
    getIndexWeekly(tradeId)
      .then(setSeries)
      .catch(() => {})
    getIndexTrend()
      .then((m) => setTrend(m.get(tradeId) ?? null))
      .catch(() => {})
  }, [tradeId])

  const isEnglishAsiaEurope = locale === 'en' && slug === 'asia-europe'
  const metaTitle = isEnglishAsiaEurope
    ? ASIA_EUROPE_TITLE
    : tName
      ? t('meta.trade.title', { name: tName.name })
      : 'Trade Lane — FreightRatesMCP'
  const metaDesc = isEnglishAsiaEurope
    ? ASIA_EUROPE_DESCRIPTION
    : tName
      ? t('meta.trade.desc', { blurb: tBlurb })
      : ''
  usePageMeta(metaTitle, metaDesc, `/trade/${slug}`)

  if (!trade || !tName)
    return (
      <div className="mx-auto max-w-2xl px-5 py-24 text-center text-muted">
        {t('trade.unknown')}{' '}
        <T to="/rate-index" className="text-link hover:underline">
          {t('trade.seeIndex')}
        </T>
      </div>
    )

  const latest = series[series.length - 1]

  return (
    <div className="mx-auto max-w-6xl px-5 pt-12">
      <nav className="text-xs text-muted">
        <T to="/rate-index" className="hover:text-link">
          {t('trade.crumb')}
        </T>{' '}
        / {tName.short}
      </nav>
      <h1 className="mt-3 font-display text-4xl font-light">
        {isEnglishAsiaEurope ? ASIA_EUROPE_HEADING : `${tName.name} ${t('trade.suffix')}`}
      </h1>
      <p className="mt-3 max-w-3xl text-muted">
        {isEnglishAsiaEurope ? ASIA_EUROPE_ANSWER : tBlurb}
      </p>

      {latest && (
        <div className="mt-6 grid gap-3 sm:grid-cols-4">
          <Stat
            label={t('trade.stat.median')}
            value={fmtPrice(latest.median_40hc)}
            accent
            trend={trend}
          />
          <Stat label={t('trade.stat.avg20')} value={fmtPrice(latest.avg_20gp)} />
          <Stat
            label={t('trade.stat.transit')}
            value={latest.avg_transit_days ? `${latest.avg_transit_days}d` : '—'}
          />
          <Stat label={t('trade.stat.lanes')} value={String(latest.lane_count)} />
        </div>
      )}

      <h2 className="mt-10 font-display text-2xl font-semibold">{t('trade.lanesOn')}</h2>
      {lanes.length === 0 ? (
        <div className="mt-4 rounded-xl border border-border-strong bg-surface-0 p-8 text-center text-muted">
          {t('lanes.loading')}
        </div>
      ) : (
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {lanes.map((l) => (
            <RateCard key={`${l.pol_code}-${l.pod_code}`} lane={l} />
          ))}
        </div>
      )}
      <div className="mt-4">
        <SponsorSlot context={trade.short} />
      </div>
      {isEnglishAsiaEurope && (
        <section className="mt-12 max-w-3xl pb-16">
          <h2 className="font-display text-2xl font-semibold">
            Asia to Europe container rate questions
          </h2>
          <div className="mt-4 divide-y divide-border-strong border-border-strong border-y">
            {ASIA_EUROPE_FAQ.map((item) => (
              <details key={item.q} className="py-4">
                <summary className="cursor-pointer font-medium">{item.q}</summary>
                <p className="mt-3 text-muted text-sm leading-relaxed">{item.a}</p>
              </details>
            ))}
          </div>
        </section>
      )}
    </div>
  )
}

function Stat({
  label,
  value,
  accent,
  trend,
}: { label: string; value: string; accent?: boolean; trend?: IndexTrend | null }) {
  return (
    <div className="rounded-lg border border-border-strong bg-surface-0 p-4">
      <div className="flex items-center justify-between">
        <div className="text-xs text-muted">{label}</div>
        {trend && <TrendPill trend={trend} />}
      </div>
      <div className={`mt-1 font-display text-2xl tnum ${accent ? 'text-signal' : ''}`}>
        {value}
      </div>
    </div>
  )
}
